import { useState, useEffect } from "react";

// helper to dynamically load external scripts
async function loadScript(src) {
  return new Promise((resolve, reject) => {
    // if already loaded, skip
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

export const useFaceDetection = () => {
  const [model, setModel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        // 1. load MediaPipe libraries dynamically
        await loadScript("https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js");
        await loadScript("https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js");

        // 2. wait until FaceMesh is defined
        let attempts = 0;
        while (typeof window.FaceMesh === "undefined" && attempts < 50) {
          await new Promise(r => setTimeout(r, 100));
          attempts++;
        }

        if (typeof window.FaceMesh === "undefined") {
          throw new Error("MediaPipe FaceMesh failed to load");
        }

        // 3. create the FaceMesh model instance
        const faceMesh = new window.FaceMesh({
          locateFile: (file) =>
            `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
        });

        faceMesh.setOptions({
          maxNumFaces: 1,
          refineLandmarks: false,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
          cameraFps: 20,
        });

        if (mounted) {
          faceMesh.onResults(() => {}); // placeholder; component will set later
          setModel(faceMesh);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Face detection load error:", err);
        if (mounted) {
          setError(err.message);
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      mounted = false;
    };
  }, []);

  return { model, isLoading, error };
};
